import React, { useCallback, useState, useEffect, useRef } from 'react'
import { View, Button, PollDetailsModal } from '@/components'
import { createStyles } from '@codeleap/styles'
import { textVariant } from '@/config'
import {
  usePollContext,
  useTranslationContext,
} from 'stream-chat-react'

export const PollActions = ({ isMyMessage }: { isMyMessage: boolean }) => {
  const { poll } = usePollContext()
  const { t } = useTranslationContext()
  const [showResults, setShowResults] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleCloseResults = useCallback(() => {
    if (mountedRef.current) {
      setShowResults(false)
    }
  }, [])

  const handleOpenResults = useCallback(() => {
    if (mountedRef.current) {
      setShowResults(true)
    }
  }, [])

  return (
    <>
      <View style={[styles.actions, styles.actionsSingle]}>
        <Button
          text={t ? t('View votes') : 'View votes'}
          style={isMyMessage ? styles.viewVotesButtonMine : styles.viewVotesButton}
          debugName='Poll:ViewVotes'
          onPress={handleOpenResults}
        />
      </View>

      <PollDetailsModal poll={poll} visible={showResults} onClose={handleCloseResults} />
    </>
  )
}

const styles = createStyles(theme => ({
  container: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.5),
    ...theme.spacing.padding(1.5),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.colors.bgDefault,
  },
  containerMine: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  header: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.25),
  },
  title: {
    ...textVariant('p3'),
    fontWeight: 600,
    color: theme.colors.textHeadingRegular,
  },
  titleMine: {
    color: theme.baseColors.neutralSolid0,
  },
  subtitle: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  subtitleMine: {
    color: theme.baseColors.neutralSolid200,
  },
  optionsWrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(1),
  },
  actions: {
    ...theme.presets.row,
    ...theme.spacing.gap(1),

    [theme.media.down('mobile')]: {
      ...theme.spacing.gap(0.75),
    },
  },
  actionsSingle: {
    ...theme.presets.column,
  },
  viewVotesButtonMine: {
    touchableWrapper: {
      ...theme.presets.justifyCenter,
      ...theme.presets.alignCenter,
      minHeight: 'unset !important',
    },
    text: {
      color: theme.baseColors.onPrimarySolid300Heading,
    },
    backgroundColor: theme.baseColors.primarySolid300,
    border: 'none',
    minHeight: 28,
    paddingTop: theme.spacing.value(1),
    paddingBottom: theme.spacing.value(1),
    borderRadius: theme.radius.small,
  },
  viewVotesButton: {
    touchableWrapper: {
      ...theme.presets.justifyCenter,
      ...theme.presets.alignCenter,
      minHeight: 'unset !important',
    },
    text: {
      color: theme.baseColors.onPrimarySolid200Heading,
    },
    backgroundColor: theme.baseColors.primarySolid200,
    border: 'none',
    minHeight: 28,
    paddingTop: theme.spacing.value(1),
    paddingBottom: theme.spacing.value(1),
    borderRadius: theme.radius.small,
  },
  actionButton: {
    touchableWrapper: {
      minWidth: theme.spacing.value(10),
    },
  },
  optionRow: {
    ...theme.presets.row,
    ...theme.presets.alignStart,
    ...theme.spacing.gap(1),
    ...theme.spacing.padding(1),
    borderRadius: theme.radius.regular,
    backgroundColor: theme.baseColors.neutralBlackTransparent50,
    cursor: 'pointer',
  },
  optionRowMine: {
    backgroundColor: theme.baseColors.primaryTransparent100,
  },
  optionRowDisabled: {
    cursor: 'default',
    opacity: 0.6,
  },
  optionLeading: {
    ...theme.presets.center,
    height: theme.spacing.value(3),
  },
  checkbox: {
    ...theme.presets.center,
    ...theme.sized(2.5),
    borderRadius: theme.radius.small,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.baseColors.primarySolid500,
    backgroundColor: 'transparent',
  },
  checkboxMine: {
    borderColor: theme.baseColors.neutralSolid0,
  },
  checkboxChecked: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  checkboxCheckedMine: {
    backgroundColor: theme.baseColors.neutralSolid0,
  },
  checkboxIcon: {
    color: theme.baseColors.neutralSolid0,
    ...theme.sized(1.5),
  },
  checkboxIconMine: {
    color: theme.baseColors.primarySolid500,
  },
  optionContent: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.5),
    flex: 1,
  },
  optionHeader: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.presets.justifySpaceBetween,
    ...theme.spacing.gap(1),
  },
  optionText: {
    ...textVariant('p3'),
    color: theme.colors.textHeadingRegular,
  },
  optionTextMine: {
    color: theme.baseColors.neutralSolid0,
  },
  voteCount: {
    ...textVariant('c3'),
    color: theme.colors.textCaption,
  },
  voteCountMine: {
    color: theme.baseColors.neutralSolid0,
  },
  progressBar: {
    position: 'relative',
    width: '100%',
    height: theme.spacing.value(0.75),
    borderRadius: theme.radius.small,
    overflow: 'hidden',
    backgroundColor: theme.baseColors.neutralBlackTransparent100,
  },
  progressBarMine: {
    backgroundColor: theme.baseColors.neutralWhiteTransparent250,
  },
  progressFill: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: theme.baseColors.primarySolid500,
  },
  progressFillMine: {
    backgroundColor: theme.baseColors.neutralSolid0,
  },
  progressFillWinner: {
    backgroundColor: theme.baseColors.primarySolid700,
  },
  avatars: {
    ...theme.presets.row,
    ...theme.spacing.gap(0.5),
  },
}))
